import {
  Button,
  DetailList,
  formatDateTime,
  formatMoney,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  TextButton,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { SortableDataTable } from "@majitap/ui/arc/sortable-data-table/sortable-data-table";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Send } from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { refreshCounts } from "../../counts";
import { useRecordCrumb } from "../../crumb";
import { messageFor } from "../../error-copy";
import { openFile } from "../../files";
import { useFormCheck } from "../../form-check";
import { METHOD, PAYMENT_STATE } from "../../payments";
import {
  type Attempt,
  inFlight,
  type Payout,
  payoutActions,
  payoutActionsFor,
  payoutBadge,
  payoutQuery,
} from "../../payouts";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import { ActivityPanel } from "../shared/panels";
import { destination } from "./list";
import styles from "./payouts.module.css";

/** How long Pay must be held: money leaves. */
export const PAY_HOLD_MS = 1500;

function useRefreshPayout(id: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["payout", id] });
    await queryClient.invalidateQueries({ queryKey: ["payouts"] });
    await queryClient.invalidateQueries({ queryKey: ["activity", "payout_request", id] });
    await refreshCounts(queryClient);
  };
}

/** One payout request (P8-17, `Console / Payout` in the design): review it, pay it, follow its attempts. */
export function PayoutPage(): ReactNode {
  const { payoutId } = useParams({ from: "/console/payouts/$payoutId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const payout = useQuery(payoutQuery(payoutId));
  useRecordCrumb(payout.data?.request_number);
  if (payout.status === "pending") return <PageLoading label="Loading the request" />;
  if (payout.status === "error") {
    const error = errorOf(payout.error);
    if (error.code === "PAYOUT_REQUEST_NOT_FOUND")
      return <PageEmpty title="No such request" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => payout.refetch()} />;
  }
  return <PayoutView payout={payout.data} permissions={permissions} meId={me?.user.id} />;
}

export function PayoutView({ payout: p, permissions, meId }: { payout: Payout; permissions: string[]; meId?: string }) {
  const format = useCompanyFormat();
  const can = payoutActionsFor(p);
  const money = (v: string) => formatMoney(v, { ...format, currency: p.currency });
  const [label, tone] = payoutBadge(p);
  const [dialog, setDialog] = useState<"approve" | "reject" | { fail: Attempt } | null>(null);
  const last = p.attempts.at(-1);
  const requesterHref = p.requester.kind === "partner" ? `/partners/${p.requester.id}` : "/fleet?view=drivers";
  return (
    <>
      <PageHeader
        title={p.request_number}
        description={`${p.requester.name} (${p.requester.kind}) · ${money(p.amount)} · asked ${formatDateTime(p.created_at, format)}`}
        actions={
          can.review ? (
            <div className={styles.actions}>
              <Button variant="secondary" onClick={() => setDialog("reject")}>
                Reject
              </Button>
              <Button onClick={() => setDialog("approve")}>Approve</Button>
            </div>
          ) : undefined
        }
      />
      {p.status === "approved" && last?.status === "failed" ? (
        <Alert tone="danger" title="The last payment failed">
          {[last.failure_reason, "The money is still held; pay again when it's fixed."].filter(Boolean).join(" ")}
        </Alert>
      ) : null}
      {p.invoice_required && !p.invoice_file_id && can.attachInvoice ? (
        <Alert tone="warning" title="The eTIMS invoice is missing">
          This partner needs a KRA eTIMS invoice on its payout requests. Attach it below before approving.
        </Alert>
      ) : null}
      {p.requester.kind === "partner" && p.payout_details_approved === false && p.status !== "paid" ? (
        <Alert tone="warning" title="The partner's payout details changed since this request">
          This request pays the details it was made with ({destination(p)}). The new ones wait for approval on the
          partner's page.
        </Alert>
      ) : null}
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.card}>
            <DetailList
              items={[
                { label: "Status", value: <Badge tone={tone}>{label}</Badge> },
                {
                  label: "Requester",
                  value: (
                    <RouterLink href={requesterHref} className={styles.link}>
                      {p.requester.name}
                    </RouterLink>
                  ),
                },
                { label: "Amount", value: money(p.amount) },
                { label: "Pay to", value: destination(p) },
                ...(p.requester.kind === "partner"
                  ? [{ label: "eTIMS invoice", value: <InvoiceField payout={p} canAttach={can.attachInvoice} /> }]
                  : []),
                {
                  label: "Wallet now",
                  value: `Balance ${money(p.wallet.balance)} · held ${money(p.wallet.held_amount)} · available ${money(p.wallet.available)}`,
                },
                {
                  label: "Reviewed",
                  value: p.reviewed_at
                    ? [
                        formatDateTime(p.reviewed_at, format),
                        p.reviewed_by_user_id === meId ? "by you" : p.reviewer?.name ? `by ${p.reviewer.name}` : null,
                        p.review_notes ? `"${p.review_notes}"` : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : null,
                },
                { label: "Paid", value: p.paid_at ? formatDateTime(p.paid_at, format) : null },
              ]}
            />
          </section>
          {p.attempts.length ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Payment attempts</h2>
              <SortableDataTable
                caption="Payment attempts"
                rowKey="payment_id"
                rows={p.attempts}
                itemName={{ one: "attempt", other: "attempts" }}
                columns={[
                  {
                    key: "created_at",
                    label: "When",
                    sortable: false,
                    render: (_v, a) => formatDateTime(a.created_at, format),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    sortable: false,
                    render: (_v, a) => (
                      <Link to="/payments/$paymentId" params={{ paymentId: a.payment_id }} className={styles.link}>
                        {a.reference}
                      </Link>
                    ),
                  },
                  {
                    key: "method",
                    label: "How",
                    sortable: false,
                    render: (_v, a) => METHOD[a.method as keyof typeof METHOD] ?? a.method,
                  },
                  {
                    key: "provider_reference",
                    label: "M-Pesa receipt",
                    sortable: false,
                    render: (_v, a) => a.provider_reference ?? "—",
                  },
                  {
                    key: "status",
                    label: "Status",
                    sortable: false,
                    render: (_v, a) => {
                      const state = PAYMENT_STATE[a.status as keyof typeof PAYMENT_STATE] ?? [a.status, "neutral"];
                      return <Badge tone={state[1]}>{state[0]}</Badge>;
                    },
                  },
                  {
                    key: "failure_reason",
                    label: "Note",
                    sortable: false,
                    render: (_v, a) =>
                      inFlight(a) ? (
                        <TextButton onClick={() => setDialog({ fail: a })}>Mark failed</TextButton>
                      ) : (
                        (a.failure_reason ?? "—")
                      ),
                  },
                ]}
              />
              {can.markFailed ? (
                <p className={styles.muted}>
                  M-Pesa hasn't answered the last attempt. Check the M-Pesa portal first; if no money went out, mark it
                  failed and pay again.
                </p>
              ) : null}
            </section>
          ) : null}
          {permissions.includes("staff.manage") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activity</h2>
              <ActivityPanel targetType="payout_request" id={p.id} label="What happened to this request" />
            </section>
          ) : null}
        </div>
        <aside className={styles.side}>{can.pay ? <PayPanel payout={p} /> : null}</aside>
      </div>
      {dialog === "approve" || dialog === "reject" ? (
        <ReviewDialog payout={p} decision={dialog} onClose={() => setDialog(null)} />
      ) : null}
      {dialog && typeof dialog === "object" ? (
        <FailDialog payout={p} attempt={dialog.fail} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

/**
 * The partner's eTIMS invoice: open it (a fresh link each time), or attach or replace it while
 * the request is still open, as a PDF of at most 10 MB.
 */
function InvoiceField({ payout: p, canAttach }: { payout: Payout; canAttach: boolean }) {
  const refresh = useRefreshPayout(p.id);
  const input = useRef<HTMLInputElement>(null);
  const open = useMutation({ mutationFn: () => openFile(p.invoice_file_id as string) });
  const attach = useMutation({
    mutationFn: (file: File) => payoutActions.attachInvoice(p.id, file),
    onSuccess: refresh,
  });
  const error = open.error ?? attach.error;
  return (
    <span className={styles.invoice}>
      {p.invoice_file_id ? (
        <TextButton disabled={open.isPending} onClick={() => open.mutate()}>
          Open the invoice
        </TextButton>
      ) : (
        <span className={styles.muted}>{p.invoice_required ? "Missing" : "None attached"}</span>
      )}
      {canAttach ? (
        <>
          <TextButton disabled={attach.isPending} onClick={() => input.current?.click()}>
            {attach.isPending ? "Attaching…" : p.invoice_file_id ? "Replace" : "Attach a PDF"}
          </TextButton>
          <input
            ref={input}
            type="file"
            accept="application/pdf"
            hidden
            aria-label="eTIMS invoice PDF"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) attach.mutate(file);
            }}
          />
        </>
      ) : null}
      {error ? <span className={styles.muted}>{messageFor(errorOf(error))}</span> : null}
    </span>
  );
}

/**
 * Approve or reject. Rejecting releases the held money. Someone can't review their own request
 * (`SAME_SUBMITTER_AND_REVIEWER`): the API's refusal shows as it is.
 */
function ReviewDialog({
  payout,
  decision,
  onClose,
}: {
  payout: Payout;
  decision: "approve" | "reject";
  onClose: () => void;
}) {
  const refresh = useRefreshPayout(payout.id);
  const [notes, setNotes] = useState("");
  const review = useMutation({
    mutationFn: () => payoutActions[decision](payout.id, notes.trim() || undefined),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={decision === "approve" ? `Approve ${payout.request_number}?` : `Reject ${payout.request_number}?`}
        description={
          decision === "approve"
            ? "It waits under To pay. Nothing is sent until someone pays it."
            : "The held money goes back to their available balance."
        }
      >
        <div className={styles.form}>
          <Textarea
            label={decision === "approve" ? "Note (optional)" : "Reason (optional)"}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
          {review.error ? <ReviewRefusal error={review.error} /> : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={decision === "reject" ? "danger" : "primary"}
              loading={review.isPending}
              onClick={() => review.mutate()}
            >
              {decision === "approve" ? "Approve" : "Reject"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** The API's refusal, as it says it ("You asked for this payout, so someone else has to review it."). */
export function ReviewRefusal({ error }: { error: unknown }) {
  return (
    <Alert tone="danger" title="Not done">
      {messageFor(errorOf(error))}
    </Alert>
  );
}

/**
 * Pay (`Console / Payout` › Pay): by M-Pesa to the snapshotted number, or record a payment made
 * another way with its reference. Either way, a press and hold.
 */
export function PayPanel({
  payout,
}: {
  payout: Pick<Payout, "id" | "amount" | "currency" | "payout_method" | "payout_destination" | "status" | "attempts">;
}) {
  const format = useCompanyFormat();
  const refresh = useRefreshPayout(payout.id);
  const can = payoutActionsFor(payout);
  const [how, setHow] = useState<"mpesa" | "other">(can.payByMpesa ? "mpesa" : "other");
  const [reference, setReference] = useState("");
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const ready = how === "mpesa" || reference.trim().length > 0;
  const pay = useMutation({
    mutationFn: () => payoutActions.pay(payout.id, how === "other" ? reference.trim() : undefined),
    onSuccess: refresh,
    onError: async () => {
      setConfirmed(false);
      // a refused B2C still records its failed attempt
      await refresh();
    },
  });
  const amount = formatMoney(payout.amount, { ...format, currency: payout.currency });
  return (
    <section className={styles.card} aria-label="Pay">
      <h2 className={styles.sectionTitle}>Pay</h2>
      {can.payByMpesa ? (
        <SegmentedControl
          label="How"
          value={how}
          onValueChange={(v) => setHow(v as "mpesa" | "other")}
          options={[
            { value: "mpesa", label: "Send by M-Pesa" },
            { value: "other", label: "Already paid" },
          ]}
        />
      ) : null}
      {how === "mpesa" ? (
        <p className={styles.muted}>
          {amount} goes to {destination(payout)} now. The request is paid when M-Pesa confirms.
        </p>
      ) : (
        <>
          <p className={styles.muted}>Paid by bank transfer, or from the M-Pesa phone: record it here.</p>
          <Input
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="The bank or M-Pesa reference"
          />
        </>
      )}
      <HoldToConfirm
        label={how === "mpesa" ? "Hold to pay" : "Hold to mark paid"}
        confirmedLabel={how === "mpesa" ? "Sent" : "Paid"}
        tone="neutral"
        icon={<Send size={16} strokeWidth={1.75} />}
        duration={PAY_HOLD_MS}
        disabled={!ready || pay.isPending}
        confirmed={confirmed}
        onConfirm={() => {
          setConfirmed(undefined);
          pay.mutate();
        }}
      />
      {pay.error ? (
        <Alert tone="danger" title="Not paid">
          {messageFor(errorOf(pay.error))}
        </Alert>
      ) : null}
    </section>
  );
}

/** An attempt M-Pesa never answered: marked failed (after checking the portal) so it can be paid again. */
function FailDialog({ payout, attempt, onClose }: { payout: Payout; attempt: Attempt; onClose: () => void }) {
  const refresh = useRefreshPayout(payout.id);
  const [reason, setReason] = useState("");
  const check = useFormCheck();
  const errors = {
    reason: !reason.trim() ? "Say why it failed." : reason.trim().length > 500 ? "At most 500 characters." : undefined,
  };
  const fail = useMutation({
    mutationFn: () => payoutActions.failAttempt(payout.id, attempt.payment_id, reason.trim()),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Mark ${attempt.reference} failed?`}
        description="Only when the M-Pesa portal shows no money went out. The request can then be paid again."
      >
        <div className={styles.form}>
          <Textarea
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            error={check.show(errors.reason)}
          />
          {fail.error ? (
            <Alert tone="danger" title="Not marked">
              {messageFor(errorOf(fail.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="danger" loading={fail.isPending} onClick={() => check.submit(errors, () => fail.mutate())}>
              Mark failed
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
